/**
 * React bindings for @zero/auth.
 *
 * Provides AuthProvider component, useAuth hook, guard components,
 * and the createAuthAxiosInstance helper.
 */

import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import { AuthManager } from "../core/auth-manager";
import type {
  AuthError,
  AuthScope,
  AuthScopeKind,
  AuthStrategy,
  EngineConfig,
  ScopedAuthorizationGrant,
  StartLoginOptions,
  UserInfo,
} from "../core/types";

// ─── Types ─────────────────────────────────────────────────────────────

export interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  /** Opaque namespace for principal-scoped client caches. */
  authEpoch: string | null;
  /** Canonical principal + grants fingerprint for authorization-scoped caches. */
  authorizationFingerprint: string | null;
  user: UserInfo | null;
  permissions: readonly string[];
  roles: readonly string[];
  authorizationGrants: readonly ScopedAuthorizationGrant[];
  scope: AuthScope | null;
  error: AuthError | null;

  logout: () => Promise<void>;
  startLogin: (options?: StartLoginOptions) => Promise<void>;
  handleCallback: () => Promise<void>;

  getAccessToken: () => string | null;
  getAuthHeaders: () => Record<string, string>;

  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
  hasAnyPermission: (...permissions: string[]) => boolean;
  hasAllPermissions: (...permissions: string[]) => boolean;
  hasAllPermissionsInScope: (requiredScope: AuthScope, ...permissions: string[]) => boolean;
  hasAllPermissionsInAnyScope: (requiredKind: AuthScopeKind, ...permissions: string[]) => boolean;
  hasAllPermissionsForResource: (
    resourceScope: AuthScope,
    minimumKind: AuthScopeKind | undefined,
    ...permissions: string[]
  ) => boolean;
  hasScope: (kind: AuthScopeKind) => boolean;
}

export interface AuthProviderProps {
  /** Auth strategy created by createOAuth2PKCEProvider() etc. Required unless manager is provided. */
  provider?: AuthStrategy;
  /** Optional engine configuration (storage, refresh buffer, etc.) */
  config?: EngineConfig;
  /** Optional pre-created AuthManager. If provided, provider/config are ignored for manager creation. */
  manager?: AuthManager;
  children: ReactNode;
}

function createAuthorizationFingerprint(user: UserInfo): string {
  const grants = user.authorizationGrants
    .map((grant) =>
      JSON.stringify([
        grant.role,
        grant.scope.kind,
        grant.scope.targetId,
        grant.scope.kind === "realm" ? (grant.scope.tenantId ?? null) : null,
        [...grant.permissions].sort(),
      ]),
    )
    .sort();
  return JSON.stringify([user.sub, user.realmId, user.tenantId, grants]);
}

export interface AuthGuardProps {
  /** Content to show when unauthenticated. Defaults to calling startLogin(). */
  fallback?: ReactNode;
  children: ReactNode;
}

export interface PermissionGuardProps {
  /**
   * Permission(s) required.
   *
   * Semantics:
   * - `string` — caller must hold this single permission.
   * - `readonly string[]` — caller must hold **every** entry (AND). This is
   *   the contract idaas-admin's NavConfig depends on: a route that requires
   *   `[A, B]` admits only callers with both. Express OR by composing
   *   multiple `<PermissionGuard>` siblings — keeping the manifest
   *   auditable rather than hiding OR inside one guard.
   */
  permission: string | readonly string[];
  /** Optional minimum scope that must be satisfied by the same grant. */
  requiredScope?: AuthScopeKind;
  /** Exact resource scope used for target-aware authorization. */
  resourceScope?: AuthScope;
  /** Content to show when permission is missing. */
  fallback?: ReactNode;
  children: ReactNode;
}

export interface RoleGuardProps {
  /** Role(s) required — any one match is sufficient (OR semantics). */
  role: string | string[];
  /** Content to show when role is missing. */
  fallback?: ReactNode;
  children: ReactNode;
}

export interface ScopeGuardProps {
  /**
   * Minimum scope required. Admission follows the partial order
   * `platform ⊃ tenant ⊃ realm`: declaring `"tenant"` admits platform-
   * scoped callers as well; declaring `"platform"` admits only platform
   * callers. `useAuth().scope === null` (unknown scope) fails closed —
   * no admission regardless of `scope` prop.
   */
  scope: AuthScopeKind;
  /** Content to show when scope is insufficient. */
  fallback?: ReactNode;
  children: ReactNode;
}

// ─── Context ───────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// ─── AuthProvider Component ────────────────────────────────────────────

export function AuthProvider({ provider, config, manager: externalManager, children }: AuthProviderProps): ReactNode {
  // Internal manager is created lazily on first render and lives for the
  // component's lifetime. We deliberately do NOT react to `provider`/`config`
  // changes — runtime strategy swaps are undefined; callers remount via `key`.
  // (Previous implementation used `useMemo` whose deps included `config`, so a
  // fresh object literal on every render would destroy + recreate the manager,
  // tearing down refresh timers and BroadcastChannel.)
  const internalManagerRef = useRef<AuthManager | null>(null);
  const internalManagerLifecycleRef = useRef(0);
  if (externalManager === undefined && internalManagerRef.current === null) {
    if (!provider) {
      throw new Error("AuthProvider requires either a 'manager' or 'provider' prop");
    }
    internalManagerRef.current = new AuthManager(provider, config);
  }

  const manager: AuthManager = externalManager ?? internalManagerRef.current!;

  // Init on mount (and when an external manager is swapped in).
  useEffect(() => {
    // init() handles its own failure via state transitions; the .catch only
    // suppresses the unhandled-rejection warning for unexpected throws.
    manager.init().catch(() => {});
  }, [manager]);

  // Destroy the internal manager on unmount. External managers are owned by
  // the caller and are not torn down here.
  useEffect(() => {
    if (externalManager !== undefined) return;
    internalManagerLifecycleRef.current += 1;
    const lifecycle = internalManagerLifecycleRef.current;
    const ownedManager = manager;
    return () => {
      queueMicrotask(() => {
        if (internalManagerLifecycleRef.current === lifecycle) {
          ownedManager.destroy();
        }
      });
    };
  }, [externalManager, manager]);

  // Subscribe to state changes via useSyncExternalStore
  const state = useSyncExternalStore(
    (callback) => manager.subscribe(callback),
    () => manager.getState(),
    () => manager.getState(),
  );

  // Stable action functions — these delegate to manager and don't depend on state.
  // Keeping them stable prevents useEffect re-fires in consumers (e.g. OAuth callback page).
  const actions = useMemo(
    () => ({
      logout: () => manager.logout(),
      startLogin: (options?: StartLoginOptions) => manager.startLogin(options),
      handleCallback: () => manager.handleCallback(),
      getAccessToken: () => manager.getAccessToken(),
      getAuthHeaders: () => manager.getAuthHeaders(),
      hasPermission: (p: string) => manager.hasPermission(p),
      hasRole: (r: string) => manager.hasRole(r),
      hasAnyPermission: (...ps: string[]) => manager.hasAnyPermission(...ps),
      hasAllPermissions: (...ps: string[]) => manager.hasAllPermissions(...ps),
      hasAllPermissionsInScope: (requiredScope: AuthScope, ...ps: string[]) =>
        manager.hasAllPermissionsInScope(requiredScope, ...ps),
      hasAllPermissionsInAnyScope: (requiredKind: AuthScopeKind, ...ps: string[]) =>
        manager.hasAllPermissionsInAnyScope(requiredKind, ...ps),
      hasAllPermissionsForResource: (
        resourceScope: AuthScope,
        minimumKind: AuthScopeKind | undefined,
        ...ps: string[]
      ) => manager.hasAllPermissionsForResource(resourceScope, minimumKind, ...ps),
      hasScope: (k: AuthScopeKind) => manager.hasScope(k),
    }),
    [manager],
  );

  const value = useMemo<AuthContextValue>(() => {
    const isInitializing = state.status === "initializing";
    const isAuthenticated = state.status === "authenticated" || state.status === "refreshing";
    const isLoading = isInitializing || state.status === "refreshing";
    const user = isAuthenticated ? state.user : state.status === "error" ? state.user : null;
    const permissions = user?.permissions ?? [];
    const roles = user?.roles ?? [];
    const authorizationGrants = user?.authorizationGrants ?? [];
    const authorizationFingerprint = isAuthenticated && user ? createAuthorizationFingerprint(user) : null;
    const scope = user?.scope ?? null;
    const error = state.status === "error" ? state.error : null;

    return {
      isAuthenticated,
      isLoading,
      authEpoch: manager.getSessionEpoch(),
      authorizationFingerprint,
      user,
      permissions,
      roles,
      authorizationGrants,
      scope,
      error,
      ...actions,
    };
  }, [state, actions, manager]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ─── useAuth Hook ──────────────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

// ─── Permission / Role / Scope Hooks ───────────────────────────────────

export function usePermissions() {
  const {
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasAllPermissionsInScope,
    hasAllPermissionsInAnyScope,
    hasAllPermissionsForResource,
  } = useAuth();
  return {
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasAllPermissionsInScope,
    hasAllPermissionsInAnyScope,
    hasAllPermissionsForResource,
  };
}

export function useRoles() {
  const { roles, hasRole } = useAuth();
  return { roles, hasRole };
}

export function useScope() {
  const { scope, hasScope } = useAuth();
  return { scope, hasScope };
}

// ─── Guard Components ──────────────────────────────────────────────────

export function AuthGuard({ fallback, children }: AuthGuardProps): ReactNode {
  const { isAuthenticated, isLoading, startLogin } = useAuth();
  const loginAttemptedRef = useRef(false);

  // Trigger the redirect as a side effect (after render) rather than during
  // render — calling startLogin() during render violates React purity and
  // would fire multiple times under StrictMode concurrent rendering.
  useEffect(() => {
    if (isLoading || isAuthenticated || fallback !== undefined) {
      loginAttemptedRef.current = false;
      return;
    }
    if (loginAttemptedRef.current) return;
    loginAttemptedRef.current = true;
    void startLogin();
  }, [isLoading, isAuthenticated, fallback, startLogin]);

  if (isLoading) {
    return null;
  }

  if (!isAuthenticated) {
    return fallback ?? null;
  }

  return children;
}

export function PermissionGuard({
  permission,
  requiredScope,
  resourceScope,
  fallback,
  children,
}: PermissionGuardProps): ReactNode {
  const { hasAllPermissions, hasAllPermissionsInAnyScope, hasAllPermissionsForResource } = useAuth();
  // AND semantics: every required permission must be present. Single-string
  // form is a degenerate case (array of one). See PermissionGuardProps doc.
  const perms = Array.isArray(permission) ? permission : [permission];

  const allowed = resourceScope
    ? hasAllPermissionsForResource(resourceScope, requiredScope, ...perms)
    : requiredScope
      ? hasAllPermissionsInAnyScope(requiredScope, ...perms)
      : hasAllPermissions(...perms);

  if (!allowed) {
    return fallback ?? null;
  }

  return children;
}

export function RoleGuard({ role, fallback, children }: RoleGuardProps): ReactNode {
  const { roles } = useAuth();
  const requiredRoles = Array.isArray(role) ? role : [role];
  const hasRequiredRole = requiredRoles.some((r) => roles.includes(r));

  if (!hasRequiredRole) {
    return fallback ?? null;
  }

  return children;
}

export function ScopeGuard({ scope: required, fallback, children }: ScopeGuardProps): ReactNode {
  const { hasScope } = useAuth();

  if (!hasScope(required)) {
    return fallback ?? null;
  }

  return children;
}

// ─── Hosted Login Components ───────────────────────────────────────────

export type { IframeLoginModalProps, PopupLoginButtonProps } from "./hosted-login";
export { IframeLoginModal, PopupLoginButton } from "./hosted-login";
